import React, { useState } from "react";
import styled from "styled-components";
import { TemplateState } from "../types/templates";
import TemplateSelector from "./TemplateSelector";
import { useTheme } from "../contexts/ThemeContext";

interface TemplateManagerProps {
  isOpen: boolean;
  onClose: () => void;
  onTemplateSelect: (states: TemplateState[]) => void;
  currentTemplateId?: string;
  isApproved?: boolean;
  isPublic?: boolean;
}

const ModalOverlay = styled.div<{ $isOpen: boolean; $isDarkMode: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${(props) => (props.$isDarkMode ? "rgba(0, 0, 0, 0.8)" : "rgba(0, 0, 0, 0.5)")};
  display: ${(props) => (props.$isOpen ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  animation: ${(props) => (props.$isOpen ? "fadeIn 0.2s ease" : "none")};

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContent = styled.div<{ $isDarkMode: boolean }>`
  background: ${(props) => (props.$isDarkMode ? "#1a1a1a" : "#ffffff")};
  border-radius: 12px;
  box-shadow: 0 20px 40px ${(props) => (props.$isDarkMode ? "rgba(0, 0, 0, 0.5)" : "rgba(0, 0, 0, 0.15)")};
  max-width: 90vw;
  max-height: 90vh;
  width: 1200px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: slideUp 0.3s ease;

  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const ModalHeader = styled.div<{ $isDarkMode: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  border-bottom: 1px solid ${(props) => (props.$isDarkMode ? "#2d2d2d" : "#e1e8ed")};
  background: ${(props) => (props.$isDarkMode ? "#1e1e1e" : "#f8f9fa")};
  flex-shrink: 0;
`;

const ModalTitle = styled.h2<{ $isDarkMode: boolean }>`
  margin: 0;
  color: ${(props) => (props.$isDarkMode ? "#ffffff" : "#2c3e50")};
  font-size: 18px;
  font-weight: 600;
`;

const CloseButton = styled.button<{ $isDarkMode: boolean }>`
  background: none;
  border: none;
  font-size: 24px;
  color: ${(props) => (props.$isDarkMode ? "#b0b0b0" : "#7f8c8d")};
  cursor: pointer;
  padding: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) => (props.$isDarkMode ? "#2d2d2d" : "#e1e8ed")};
    color: ${(props) => (props.$isDarkMode ? "#ffffff" : "#2c3e50")};
  }
`;

const ModalBody = styled.div<{ $isDarkMode: boolean }>`
  flex: 1;
  overflow-y: auto;
  padding: 0;
  background: ${(props) => (props.$isDarkMode ? "#1a1a1a" : "#ffffff")};
`;

const ModalFooter = styled.div<{ $isDarkMode: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 20px;
  border-top: 1px solid ${(props) => (props.$isDarkMode ? "#2d2d2d" : "#e1e8ed")};
  background: ${(props) => (props.$isDarkMode ? "#1e1e1e" : "#f8f9fa")};
  flex-shrink: 0;
`;

const FooterTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SelectedInfo = styled.div`
  flex: 1;
`;

const SelectedCount = styled.span<{ $isDarkMode: boolean }>`
  background: #2196f3;
  color: white;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 10px;
`;

const CancelButton = styled.button<{ $isDarkMode: boolean }>`
  background: ${(props) => (props.$isDarkMode ? "#404040" : "#95a5a6")};
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: ${(props) => (props.$isDarkMode ? "#505050" : "#7f8c8d")};
  }
`;

const ImportButton = styled.button<{ $disabled: boolean }>`
  background: ${(props) => (props.$disabled ? "#bdc3c7" : "#27ae60")};
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  transition: background-color 0.2s ease;

  &:hover:not(:disabled) {
    background: #229954;
  }

  &:disabled {
    opacity: 0.6;
  }
`;

const TemplateInfoFooter = styled.div<{ $isDarkMode: boolean; $isApproved?: boolean; $isPublic?: boolean }>`
  padding-top: 8px;
  border-top: 1px solid ${(props) => (props.$isDarkMode ? "#2d2d2d" : "#e1e8ed")};
`;

const TemplateManager: React.FC<TemplateManagerProps> = ({
  isOpen,
  onClose,
  onTemplateSelect,
  currentTemplateId,
  isApproved,
  isPublic,
}) => {
  const { isDarkMode } = useTheme();
  const [selectedStates, setSelectedStates] = useState<TemplateState[]>([]);
  const [isInfoExpanded, setIsInfoExpanded] = useState<boolean>(false);

  const handleTemplateSelect = (states: TemplateState[]) => {
    setSelectedStates(states);
  };

  const handleApplyTemplate = () => {
    if (selectedStates.length > 0) {
      onTemplateSelect(selectedStates);
      onClose();
    }
  };

  const handleCancel = () => {
    setSelectedStates([]);
    onClose();
  };

  // Close on overlay click
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleCancel();
    }
  };

  const canBecomeTemplate = isApproved && isPublic;

  if (!isOpen) return null;

  return (
    <ModalOverlay
      $isOpen={isOpen}
      $isDarkMode={isDarkMode}
      onClick={handleOverlayClick}
    >
      <ModalContent $isDarkMode={isDarkMode}>
        <ModalHeader $isDarkMode={isDarkMode}>
          <ModalTitle $isDarkMode={isDarkMode}>Import Template Data</ModalTitle>
          <CloseButton
            $isDarkMode={isDarkMode}
            onClick={handleCancel}
            aria-label="Close template manager"
          >
            ×
          </CloseButton>
        </ModalHeader>

        <ModalBody $isDarkMode={isDarkMode}>
          <TemplateSelector
            onTemplateSelect={handleTemplateSelect}
            selectedTemplateId={currentTemplateId}
            isDarkMode={isDarkMode}
            isApproved={isApproved}
            isPublic={isPublic}
            showInfoBanner={false}
          />
        </ModalBody>

        <ModalFooter $isDarkMode={isDarkMode}>
          <FooterTop>
            <SelectedInfo>
              {selectedStates.length > 0 && (
                <SelectedCount $isDarkMode={isDarkMode}>
                  {selectedStates.length} state
                  {selectedStates.length !== 1 ? "s" : ""} selected for import
                </SelectedCount>
              )}
            </SelectedInfo>

            <ModalActions>
              <CancelButton $isDarkMode={isDarkMode} onClick={handleCancel}>
                Cancel
              </CancelButton>
              <ImportButton
                $disabled={selectedStates.length === 0}
                onClick={handleApplyTemplate}
              >
                Import Selected States
              </ImportButton>
            </ModalActions>
          </FooterTop>
          <TemplateInfoFooter $isDarkMode={isDarkMode} $isApproved={isApproved} $isPublic={isPublic}>
            {canBecomeTemplate ? (
              <InfoBannerCompact
                $isDarkMode={isDarkMode}
                $variant="success"
                $expanded={isInfoExpanded}
                onClick={() => setIsInfoExpanded(!isInfoExpanded)}
              >
                <InfoIconCompact>✨</InfoIconCompact>
                <InfoContentCompact>
                  <InfoTextShortCompact>Your map is eligible for templates!</InfoTextShortCompact>
                  {isInfoExpanded && (
                    <InfoTextExpandedCompact>
                      Since your map is approved and public, it can be added to the template library for others to use.
                    </InfoTextExpandedCompact>
                  )}
                </InfoContentCompact>
                <ExpandIconCompact $expanded={isInfoExpanded}>▶</ExpandIconCompact>
              </InfoBannerCompact>
            ) : (
              <InfoBannerCompact
                $isDarkMode={isDarkMode}
                $expanded={isInfoExpanded}
                onClick={() => setIsInfoExpanded(!isInfoExpanded)}
              >
                <InfoIconCompact>ℹ️</InfoIconCompact>
                <InfoContentCompact>
                  <InfoTextShortCompact>Want your map to become a template?</InfoTextShortCompact>
                  {isInfoExpanded && (
                    <InfoTextExpandedCompact>
                      First request approval for your map. Once approved, make it public. Approved and public maps can be added to the template library.
                    </InfoTextExpandedCompact>
                  )}
                </InfoContentCompact>
                <ExpandIconCompact $expanded={isInfoExpanded}>▶</ExpandIconCompact>
              </InfoBannerCompact>
            )}
          </TemplateInfoFooter>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
};

// Compact info banner components for footer
const InfoBannerCompact = styled.button<{ $isDarkMode: boolean; $variant?: "info" | "success"; $expanded: boolean }>`
  padding: 6px 10px;
  border-radius: 4px;
  background: ${(props) => {
    if (props.$variant === "success") {
      return props.$isDarkMode ? "rgba(76, 175, 80, 0.08)" : "rgba(76, 175, 80, 0.05)";
    }
    return props.$isDarkMode ? "rgba(33, 150, 243, 0.08)" : "rgba(33, 150, 243, 0.05)";
  }};
  border: 1px solid ${(props) => {
    if (props.$variant === "success") {
      return props.$isDarkMode ? "rgba(76, 175, 80, 0.2)" : "rgba(76, 175, 80, 0.15)";
    }
    return props.$isDarkMode ? "rgba(33, 150, 243, 0.2)" : "rgba(33, 150, 243, 0.15)";
  }};
  color: ${(props) => (props.$variant === "success" 
    ? (props.$isDarkMode ? "#81c784" : "#2e7d32")
    : (props.$isDarkMode ? "#b0d4ff" : "#1565c0"))};
  font-size: 11px;
  line-height: 1.3;
  display: flex;
  align-items: flex-start;
  gap: 6px;
  cursor: pointer;
  width: 100%;
  text-align: left;
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) => {
      if (props.$variant === "success") {
        return props.$isDarkMode ? "rgba(76, 175, 80, 0.12)" : "rgba(76, 175, 80, 0.08)";
      }
      return props.$isDarkMode ? "rgba(33, 150, 243, 0.12)" : "rgba(33, 150, 243, 0.08)";
    }};
  }
`;

const InfoIconCompact = styled.span`
  font-size: 12px;
  flex-shrink: 0;
  margin-top: 1px;
`;

const InfoContentCompact = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const InfoTextShortCompact = styled.span`
  font-weight: 500;
  font-size: 11px;
`;

const InfoTextExpandedCompact = styled.div`
  margin-top: 3px;
  font-size: 10px;
  opacity: 0.85;
`;

const ExpandIconCompact = styled.span<{ $expanded: boolean }>`
  font-size: 10px;
  flex-shrink: 0;
  margin-top: 1px;
  transition: transform 0.2s ease;
  transform: ${(props) => (props.$expanded ? "rotate(90deg)" : "rotate(0deg)")};
`;

export default TemplateManager;
